<template>
  <v-container>
    <back-button :label="transPlural('booking.booking')" fallback-route="Bookings"></back-button>

    <request-state :request-promise="bookingRequest" :retry-action="reloadBooking">
      <template v-if="booking">
        <page-heading>{{ pageTitle }}</page-heading>

        <v-form>
          <v-card>
            <v-card-title>
              {{ trans('booking.mainInformation') }}
            </v-card-title>

            <v-card-text>
              <form-input
                  :label="trans('booking.status')"
                  v-model="booking.status"
                  cols="3"
                  cols-help="6"
              >
                <template v-slot:input="">
                  <status-badge v-if="booking.disabled" color="red lighten-3">{{ trans('general.deleted') }}</status-badge>
                  <template v-else>
                    <v-icon v-if="isOnlyCanceledInStayin" :title="trans('booking.isOnlyCanceledInStayin')" color="warning" class="pr-1">mdi-alert</v-icon>
                    <booking-status :booking="booking" />
                  </template>
                </template>
              </form-input>

              <form-input
                  v-if="canSeeAllDetails"
                  :label="trans('booking.noOverwrite')"
                  v-model="booking.updated"
                  cols="3"
                  cols-help="6"
                  type="checkbox"
                  hide-details
              />

              <form-input
                  v-if="canSeeAllDetails"
                  :label="trans('booking.contract')"
                  cols="3"
                  cols-help="6"
              >
                <template v-slot:input>
                  <v-btn small v-if="booking.contractLink" @click="downloadRentalAgreement" :loading="downloadingRentalAgreement">{{ trans('general.download') }}</v-btn>
                  <span v-else>{{ trans('booking.noContract') }}</span>
                </template>
              </form-input>

              <form-input
                  v-if="canSeeAllDetails"
                  :label="trans('booking.type')"
                  cols="3"
                  cols-help="6"
              >
                <template v-slot:input>
                  <span v-if="booking.isOnlineBooking">{{ trans('booking.isOnlineBooking') }}</span>
                  <span v-else>{{ trans('booking.isOfflineBooking') }}</span>
                </template>
              </form-input>

              <form-input
                  v-if="canSeeAllDetails"
                  :label="trans('booking.provider')"
                  cols="3"
                  cols-help="6"
              >
                <template v-slot:input>
                  <booking-provider :value="booking.provider"/>
                </template>
              </form-input>

              <form-input
                  :label="transSingular('object.object')"
                  cols="3"
                  cols-help="6"
              >
                <template v-slot:input>
                  <object-name :value="booking"/>
                </template>
              </form-input>

              <form-input
                :label="trans('booking.bookingDate')"
                v-model="booking.bookingDate"
                type="date"
                cols="3"
                cols-help="6"
                readonly
              >
              </form-input>

              <form-input
                  :label="trans('booking.bookingPeriod')"
                  cols="3"
                  cols-help="6"
              >
                <template v-slot:input>
                  <span>{{ formatDate(booking.checkinAt) }}</span>
                  <span class="ml-2 mr-2">-</span>
                  <span>{{ formatDate(booking.checkoutAt) }}</span>
                </template>
              </form-input>

              <form-input
                v-if="canSeeAllDetails"
                :readonly="!isBookingOpen"
                :label="trans('booking.provisionDate')"
                v-model="booking.provisionDate"
                type="date"
                date-type="month"
                cols="3"
                cols-help="6"
                :clearable="false"
              >
                <template v-slot:help-text>
                  <span>
                    {{ transSingular('booking.settledUpIn') }}: <settlement-year-month :value="booking.provisionSettlement"/>
                  </span>
                </template>
              </form-input>

              <form-input
                v-if="canSeeAllDetails"
                :readonly="!isBookingOpen"
                :label="trans('booking.settleDate')"
                v-model="booking.settleDate"
                type="date"
                date-type="month"
                cols="3"
                cols-help="6"
                :clearable="false"
              >
                <template v-if="booking.canceled" v-slot:input>
                  <span style="text-decoration: line-through">{{ formatDate(booking.settleDate, 'MMMM YYYY') }}</span>
                  <help-tooltip translation-key="booking.noSettleBecauseCanceledHelp"/>
                </template>

                <template v-slot:help-text>
                  <span>
                    {{ transSingular('booking.settledUpIn') }}: <settlement-year-month :value="booking.settleSettlement"/>
                    <br>
                  </span>
                  <span v-if="booking.settleDateOld">
                    {{ trans('booking.settleDateOld', {date: formatDate(booking.settleDateOld, 'MMMM YYYY')}) }}
                  </span>
                </template>
              </form-input>

              <form-input
                  v-if="canSeeAllDetails"
                  :label="trans('booking.rentalPaymentDueDate')"
                  v-model="booking.rentalPaymentDueDate"
                  type="date"
                  cols="3"
                  cols-help="6"
                  readonly
              >
              </form-input>

              <form-input
                  :label="trans('booking.guestCount')"
                  cols="3"
                  cols-help="6"
              >
                <template v-slot:input>
                  <span>{{ booking.adults }} {{ transc('booking.adults', booking.adults) }}</span>,
                  <span>{{ booking.children }} {{ transc('booking.children', booking.children) }}</span>
                </template>
              </form-input>

              <form-input
                v-if="canSeeAllDetails"
                :label="trans('booking.extendedBookingId')"
                v-model="booking.extendedBookingId"
                cols="3"
                cols-help="6"
                readonly
              >
              </form-input>

              <form-input
                  cols="3"
                  v-if="canSeeAllDetails"
              >
                <template v-slot:input>
                  <global-validation-errors :value="validationErrors" />
                  <save-button v-if="isBookingOpen" @click="updateBooking"/>
                </template>
              </form-input>
            </v-card-text>
          </v-card>

          <guest-card :guest="booking.guest" class="mt-3" />

          <v-card class="mt-3">
            <v-card-title>
              {{ trans('booking.emailInformation') }}
            </v-card-title>

            <v-card-text>
              <form-input
                :label="trans('booking.emails.owner')"
                :value="formatDate(booking.sentMailToOwner, 'LLL')"
                cols="3"
                readonly
              />
              <ajax-table
                ref="emailsTable"
                v-show="hasEmails"
                :resource="'bookings/'+booking.id+'/emails'"
                no-elevation
                :headers="[
                  {text: 'emailSettings.subject', value: 'subject'},
                  {text: 'emailSettings.to', value: 'to'},
                  {text: 'emailSettings.status', value: 'status'},
                  {text: 'emailSettings.sentAt', value: 'successfullySentAt'},
                  {text: '', value: 'actions', sortable: false},
                ]"
                :page-options="{itemsPerPage: 5}"
                :items-per-page-options="[5, 10, 20]"
                @page-loaded="setHasEmails"
              >
                <template v-slot:item.status="{ item }">
                  <email-status :email="item"></email-status>
                </template>
                <template v-slot:item.actions="{ item }">
                  <show-icon-button @click="getEmail(item)" :title="trans('emailSettings.showEmail')">
                    mdi-email-search
                  </show-icon-button>
                </template>
              </ajax-table>
            </v-card-text>
          </v-card>

          <v-card class="mt-3">
            <v-card-title>
              {{ trans('booking.extraServicesInformation') }}
            </v-card-title>

            <v-card-text>
              <v-simple-table>
                <thead>
                <tr>
                  <th>{{ trans('booking.extraService.name') }}</th>
                  <th>{{ trans('booking.extraService.count') }}</th>
                  <th>{{ trans('booking.extraService.price') }}</th>
                </tr>
                </thead>
                <tbody v-if="booking.extraServices">
                <tr v-for="(extraServices, extraServiceName) in booking.extraServices" :key="extraServiceName">
                  <td>{{ extraServiceName }}</td>
                  <td>{{ extraServices.quantity }}</td>
                  <td><currency-amount :amount="extraServices.cost" currency="EUR" /></td>
                </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>

          <v-card v-if="canSeeAllDetails" class="mt-3">
            <v-card-title>
              {{ trans('booking.paymentInformation') }}
            </v-card-title>

            <v-card-text>
              <v-simple-table>
                <thead>
                  <tr>
                    <th>{{ trans('booking.payment.type') }}</th>
                    <th>{{ trans('booking.payment.amount') }}</th>
                    <th>{{ trans('booking.payment.date') }}</th>
                  </tr>
                </thead>
                <tbody v-if="booking.payments">
                  <tr v-for="(payment, i) in booking.payments" :key="i">
                    <td>{{ trans('booking.payment.types.' + payment.type) }}</td>
                    <td><currency-amount :amount="payment.amount" currency="EUR" /></td>
                    <td>{{ formatDate(payment.date) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>

          <v-card class="mt-3" v-if="canSeeAllDetails && booking.guest && booking.guest.bankAccount">
            <v-card-title>
              {{ trans('bankAccount.bankDetails') }}
            </v-card-title>

            <v-card-text>
              <form-input
                v-if="booking.guest.bankAccount.bankBlz"
                :label="trans('bankAccount.blz')"
                v-model="booking.guest.bankAccount.bankBlz"
                cols="3"
                readonly
              />

              <form-input
                v-if="booking.guest.bankAccount.bankName"
                :label="trans('bankAccount.bankName')"
                v-model="booking.guest.bankAccount.bankName"
                cols="3"
                readonly
              />

              <form-input
                :label="trans('bankAccount.owner')"
                v-model="booking.guest.bankAccount.bankOwner"
                cols="3"
                readonly
              />

              <form-input
                v-if="booking.guest.bankAccount.bankAcc"
                :label="trans('bankAccount.accountNumber')"
                v-model="booking.guest.bankAccount.bankAcc"
                cols="3"
                readonly
              />

              <form-input
                :label="trans('bankAccount.iban')"
                v-model="booking.guest.bankAccount.bankIban"
                cols="3"
                readonly
              />

              <form-input
                :label="trans('bankAccount.bic')"
                v-model="booking.guest.bankAccount.bankSwift"
                cols="3"
                readonly
              />
            </v-card-text>
          </v-card>

          <v-card class="mt-3">
            <v-card-title>
              {{ trans('booking.notesInformation') }}
            </v-card-title>

            <v-card-text>
              <form-input
                  v-if="canSeeAllDetails"
                  :label="trans('booking.notesStayin')"
                  v-model="booking.notes"
                  cols="7"
                  cols-help="3"
                  readonly
                  class="mb-5"
                  type="textarea"
              >
              </form-input>

              <form-input
                  v-if="canSeeAllDetails"
                  :label="trans('booking.notesAdditional')"
                  v-model="booking.notesAdditional"
                  type="textarea"
                  cols="7"
                  cols-help="3"
              />

              <form-input
                  :label="trans('booking.notesCaretaker')"
                  v-model="booking.notesCaretaker"
                  type="textarea"
                  cols="7"
                  cols-help="3"
              />

              <form-input cols="3">
                <template v-slot:input>
                  <global-validation-errors :value="validationErrors" />
                  <save-button @click="updateBooking"/>
                </template>
              </form-input>
            </v-card-text>
          </v-card>

          <v-card v-if="canSeeAllDetails" class="mt-3">
            <v-card-title>
              {{ trans('booking.invoiceInformation') }}
            </v-card-title>

            <v-card-text>
                <form-input
                  :label="trans('booking.invoice.total')"
                  v-model="booking.totalWithoutDepositAndBookingFee"
                  currency="EUR"
                  cols="2"
                  readonly
                />

                <form-input
                  :error-messages="validationErrors['rentalPayment']"
                  :label="trans('booking.invoice.rent')"
                  v-model="booking.rentalPayment"
                  currency="EUR"
                  cols="2"
                  :readonly="!isBookingOpen"
                />

                <form-input
                  :error-messages="validationErrors['additionalServices']"
                  :label="trans('booking.invoice.additionalServices')"
                  v-model="booking.additionalServices"
                  currency="EUR"
                  cols="2"
                  :readonly="!isBookingOpen"
                />

                <form-input
                  :error-messages="validationErrors['petsCharge']"
                  :label="trans('booking.invoice.pets')"
                  v-model="booking.petsCharge"
                  currency="EUR"
                  cols="2"
                  :readonly="!isBookingOpen"
                />

                <form-input
                  :error-messages="validationErrors['cleaningCharge']"
                  :label="trans('booking.invoice.cleaning')"
                  v-model="booking.cleaningCharge"
                  currency="EUR"
                  cols="2"
                  :readonly="!isBookingOpen"
                />

                <form-input
                  :label="trans('booking.invoice.cancellationFee')"
                  v-model="booking.cancellationFee"
                  currency="EUR"
                  cols="2"
                  readonly
                  v-if="booking.cancellationFee"
                />

                <v-divider class="mb-3"/>

                <form-input
                  :error-messages="validationErrors['firstPayment']"
                  :label="trans('booking.invoice.prepayment')"
                  v-model="booking.firstPayment"
                  currency="EUR"
                  cols="2"
                  :readonly="!isBookingOpen"
                />

                <form-input
                  :error-messages="validationErrors['bookingFee']"
                  :label="trans('booking.invoice.bookingFee')"
                  v-model="booking.bookingFee"
                  currency="EUR"
                  cols="2"
                  :readonly="!isBookingOpen"
                />

                <form-input
                  v-if="booking.cancellationFeeExternal"
                  :label="trans('booking.invoice.cancellationFeeExternal')"
                  v-model="booking.cancellationFeeExternal"
                  currency="EUR"
                  cols="2"
                  readonly
                />

                <form-input
                  :error-messages="validationErrors['providerCommission']"
                  :label="trans('booking.invoice.providerCommission')"
                  v-model="booking.providerCommission"
                  currency="EUR"
                  cols="2"
                  :readonly="!isBookingOpen"
                />

                <form-input
                  :error-messages="validationErrors['prePaymentProviderCommission']"
                  :label="trans('booking.invoice.prePaymentProviderCommission')"
                  v-model="booking.prePaymentProviderCommission"
                  currency="EUR"
                  cols="2"
                  :readonly="!isBookingOpen"
                />

                <form-input cols="3">
                  <template v-slot:input>
                    <global-validation-errors :value="validationErrors" />
                    <save-button v-if="isBookingOpen" @click="updateBooking"/>
                  </template>
                </form-input>
            </v-card-text>
          </v-card>

          <v-card class="mt-3">
            <v-card-title>
              {{ trans('booking.consumptionInformation') }}
            </v-card-title>

            <v-card-text>
              <booking-consumptions
                :booking="booking"
                :can-edit-consumptions="canEditConsumptions"
                @save="reloadBooking"
              />
            </v-card-text>
          </v-card>

          <v-card class="mt-3" v-if="canViewMiscs">
            <v-card-title>
              {{ trans('misc.miscsBooking') }}
            </v-card-title>
            <v-card-text>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>{{ trans('misc.description') }}</th>
                    <th>{{ trans('misc.price') }}</th>
                    <th>{{ trans('misc.addCashflow') }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="misc in booking.miscs" :key="misc.miscId">
                    <td>{{ misc.description }}</td>
                    <td><currency-amount :amount="misc.price" currency="EUR" /></td>
                    <td><boolean-flag-icon :value="misc.addCashflow" /></td>
                    <td>
                      <edit-icon-button v-if="canEditMiscs" @click="showBookingMiscModalWithValue(misc)"/>
                      <delete-icon-button v-if="canEditMiscs" @click="bookingMiscToDelete = misc"/>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td colspan="3"></td>
                    <td>
                      <v-btn v-if="canEditMiscs" small @click="showBookingMiscModalWithValue({bookingId: bookingId})">
                        <v-icon left>mdi-file-plus</v-icon> {{ trans('misc.newMiscBooking') }}
                      </v-btn>
                    </td>
                  </tr>
                </tfoot>
              </v-simple-table>

            </v-card-text>
          </v-card>

          <v-card v-if="canSeeAllDetails" class="mt-3">
            <v-card-title>
              {{ trans('booking.transactionInformation') }}
            </v-card-title>

            <v-card-text v-if="booking">
              <template v-if="booking.calculatedTransaction">
                <form-input
                  v-for="(lineItem, i) in calculatedTransactionLineItems"
                  :key="i"
                  :label="lineItem.description"
                  v-model="lineItem.amount"
                  readonly
                  currency="EUR"
                  cols="2"
                  :with-positive-indicator="true"
                />

                <v-divider class="mb-3"/>

                <form-input
                  :label="trans('booking.transactionAmount')"
                  :value="booking.calculatedTransaction.resultAmount"
                  readonly
                  currency="EUR"
                  cols="2"
                />
              </template>
              <form-input
                v-if="booking.transactionErrorCodes && booking.transactionErrorCodes.length > 0"
                :label="transSingular('booking.validationErrorCodes')"
                cols="8"
              >
                <template #input>
                  <div class="mb-3 text-body-2">
                    <div
                      v-for="errorCode in booking.transactionErrorCodes"
                      :key="errorCode"
                    >
                      <booking-validation-error
                        :value="errorCode"
                        :booking="booking"
                      />
                    </div>
                  </div>
                </template>
              </form-input>
              <form-input
                  :label="transSingular('transaction.transaction')"
                  cols="8"
              >
                <template v-slot:input>
                  <v-simple-table v-if="transaction" dense>
                    <thead>
                      <tr>
                        <th>{{ trans('transaction.status') }}</th>
                        <th>{{ trans('booking.amountTransaction') }}</th>
                        <th>{{ trans('general.createdAt', {}) }}</th>
                        <th>{{ trans('transaction.downloadedAt') }}</th>
                        <th>{{ trans('transaction.mailStatus') }}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><transaction-status :value="transaction.status"/></td>
                        <td><currency-amount :amount="transaction.refundAmount" currency="EUR" /></td>
                        <td>{{ formatDate(transaction.createdAt, 'L') }}</td>
                        <td v-if="transaction.downloadedAt">{{ formatDate(transaction.downloadedAt, 'L') }}</td>
                        <td v-else>{{ trans('transaction.downloadedNever') }}</td>
                        <td><transaction-guest-mail-status :transaction="transaction" :status="transaction.guestMailStatus" /></td>
                        <td>
                          <transaction-context-menu
                            :transaction="transaction"
                            @transaction-changed="onTransactionChanged"
                          ></transaction-context-menu>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                  <template v-else>
                    <status-badge v-if="booking.refundStatus === 'MISSING'">{{ trans('transaction.transactionsMissing') }}</status-badge>
                    <status-badge v-else-if="booking.refundStatus === 'NOT_NEEDED'">{{ trans('transaction.transactionsNotNeeded') }}</status-badge>
                    <v-btn class="ml-4" small @click="createTransaction" :disabled="!booking.transactionCreatable">{{ trans('transactions.createFromBooking') }}</v-btn>
                    <v-btn  v-if="booking.refundStatus !== 'NOT_NEEDED'" class="ml-2" small @click="markTransactionAsNotNeeded">{{ trans('transactions.markAsNotNeeded') }}</v-btn>
                  </template>
                </template>
              </form-input>
            </v-card-text>
          </v-card>

          <v-card class="mt-3" v-if="canSeeAllDetails && booking.provision">
            <v-card-title>
              {{ trans('booking.provisionInformation') }}
            </v-card-title>

            <v-card-text>
              <form-input
                  :label="trans('booking.provision.net')"
                  :value="booking.provision.totalNet"
                  readonly
                  currency="EUR"
                  cols="2"
                  cols-help="7"
                  :help-text="provisionCalculationText"
              />
              <form-input
                  :label="trans('booking.provision.vat')"
                  :value="booking.provision.taxes"
                  readonly
                  currency="EUR"
                  cols="2"
                  cols-help="7"
                  :help-text="provisionTaxText"
              />
              <form-input
                  :label="trans('booking.provision.gross')"
                  :value="booking.provision.totalGross"
                  readonly
                  currency="EUR"
                  cols="2"
              />
            </v-card-text>
          </v-card>

          <survey-result-card
            v-if="canSeeAllDetails"
            :booking="booking"
            :survey-result="booking.surveyResult"
            @update="onSurveyResultUpdate"
            class="mt-3"
          />

          <v-card class="mt-3" v-if="canSeeAllDetails">
            <v-card-title>
              {{ trans('general.actions') }}
            </v-card-title>

            <v-card-text>

              <form-input
                  :label="trans('booking.validationErrorCodes')"
                  cols="6"
                  v-if="validationErrorsPresent">
                <template v-slot:input>
                  <table>
                    <tbody>
                      <tr v-for="errorCode in booking.validationErrorCodes" :key="errorCode">
                        <td>
                          <booking-validation-error :value="errorCode" />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </template>
              </form-input>

              <form-input
                  cols="6"
                  :help-text="booking.disabled ? null : trans('booking.closeHelpText')"
              >
                <template v-slot:input>

                  <div v-if="!booking.disabled">
                    <v-btn v-if="booking.status == 1" color="primary" @click="openBooking">
                      {{ trans('booking.open') }}
                    </v-btn>
                    <v-btn v-else color="primary" @click="closeBooking" :disabled="validationErrorsPresent">
                      {{ trans('booking.close') }}
                    </v-btn>

                    <v-btn v-if="!booking.canceled" @click="showCancelConfirmModal = true" class="ml-2 red--text">
                      <v-icon left>mdi-book-cancel</v-icon>{{ trans('booking.cancel') }}
                    </v-btn>
                    <v-btn v-else @click="showUncancelModal = true" class="ml-2 warning--text">
                      <v-icon left>mdi-book-cancel</v-icon>{{ trans('booking.uncancel') }}
                    </v-btn>
                  </div>

                  <div v-if="hasAnyPermission('BOOKING_EDIT')" class="mt-8">
                    <v-btn
                      v-if="booking.disabled == 1"
                      @click="restoreBooking"
                    >
                      <v-icon left>mdi-delete-off</v-icon>
                      <span>{{ trans('general.restore') }}</span>
                    </v-btn>
                    <v-btn
                      v-else
                      class="red--text"
                      @click="showDeleteConfirmModal = true"
                    >
                      <v-icon left>mdi-delete</v-icon>
                      <span>{{ trans('general.delete') }}</span>
                    </v-btn>
                  </div>
                </template>
              </form-input>
            </v-card-text>
          </v-card>

          <confirm-modal
            :show-modal.sync="showCancelConfirmModal"
            @confirm="cancelBooking"
            :title="trans('booking.cancel')"
            :confirm-button-label="trans('booking.cancel')"
            confirm-button-color="error"
          >
            {{ trans('booking.confirmCancel') }}
          </confirm-modal>

        </v-form>
      </template>
    </request-state>

    <booking-misc-addit-modal
      :initial-booking-misc="bookingMiscToUpdate"
      :show-modal.sync="showBookingMiscModal"
      :disable-add-cashflow="booking.canceled"
      @success="reloadBooking"
    />

    <booking-uncancel-modal :booking="booking" :show-modal.sync="showUncancelModal" @success="reloadBooking"/>

    <validation-errors-modal
      :show-modal.sync="showTransactionValidationErrorsModal"
      :title="trans('transactions.createFromBooking')"
      :validation-errors="transactionValidationErrors"
    />

    <confirm-deletion-modal
        :title="trans('misc.deleteMiscBooking')"
        :show-modal="bookingMiscToDelete != null"
        @confirm="deleteBookingMisc"
        @close="bookingMiscToDelete = null"
    >
      <template v-if="bookingMiscToDelete">
        <b>{{ trans('misc.description') }}: </b>
        {{ bookingMiscToDelete.description }}<br>
        <b>{{ trans('misc.price') }}: </b>
        <currency-amount style="display: inline-block" :amount="bookingMiscToDelete.price" currency="EUR"/>
      </template>
    </confirm-deletion-modal>

    <confirm-deletion-modal
      :title="trans('booking.delete')"
      :show-modal="showDeleteConfirmModal"
      @confirm="deleteBooking"
      @close="showDeleteConfirmModal = false"
    >
      <template v-if="bookingId">
        {{ trans('booking.deleteConfirm', {bookingId: bookingId}) }}
      </template>
    </confirm-deletion-modal>

    <email-modal
      :show-modal.sync="showEmailModal"
      :email="currentEmail"
    />

  </v-container>
</template>

<script>
import PageHeading from '../components/PageHeading';
import {LocaleUtils} from '../utils/locale-utils';
import {MainUtils} from '../utils/main-utils';
import {RouterUtils} from '../utils/router-utils';
import RequestState from '@/components/RequestState';
import BackButton from '@/components/BackButton';
import {BookingApi} from '@/api/booking-api';
import FormInput from '@/components/FormInput';
import BookingStatus from '@/components/BookingStatus';
import BookingProvider from '@/components/BookingProvider';
import ObjectName from '@/components/ObjectName';
import {DateUtils} from '@/utils/date-utils';
import CurrencyAmount from '@/components/CurrencyAmount';
import BookingMiscAdditModal from '@/components/BookingMiscAdditModal';
import EditIconButton from '@/components/EditIconButton';
import DeleteIconButton from '@/components/DeleteIconButton';
import ConfirmDeletionModal from '@/components/ConfirmDeletionModal';
import {MiscApi} from '@/api/misc-api';
import TransactionStatus from '@/components/TransactionStatus';
import StatusBadge from '@/components/StatusBadge';
import SaveButton from '@/components/SaveButton';
import BookingValidationError from '@/components/BookingValidationError';
import GlobalValidationErrors from '@/components/GlobalValidationErrors';
import SettlementYearMonth from '@/components/SettlementYearMonth';
import ConfirmModal from '../components/ConfirmModal';
import BooleanFlagIcon from '../components/BooleanFlagIcon';
import AjaxTable from '../components/AjaxTable';
import EmailStatus from '../components/EmailStatus';
import {EmailUtils} from '../utils/email-utils';
import EmailModal from '../components/EmailModal';
import ShowIconButton from '../components/ShowIconButton';
import ValidationErrorsModal from '../components/ValidationErrorsModal';
import TransactionGuestMailStatus from '../components/TransactionGuestMailStatus';
import TransactionContextMenu from '@/components/TransactionContextMenu';
import BookingUncancelModal from '@/components/BookingUncancelModal';
import GuestCard from '@/components/GuestCard';
import SurveyResultCard from '@/components/SurveyResultCard';
import HelpTooltip from '@/components/icons/HelpTooltip';
import BookingConsumptions from '@/components/BookingConsumptions.vue';

export default {
  name: 'BookingPage',
  mixins: [
    DateUtils,
    LocaleUtils,
    MainUtils,
    RouterUtils,
    EmailUtils
  ],
  components: {
    BookingConsumptions,
    HelpTooltip,
    SurveyResultCard,
    GuestCard,
    BookingUncancelModal,
    TransactionContextMenu,
    ValidationErrorsModal,
    ShowIconButton,
    EmailModal,
    EmailStatus,
    AjaxTable,
    TransactionGuestMailStatus,
    BooleanFlagIcon,
    ConfirmModal,
    SettlementYearMonth,
    GlobalValidationErrors,
    BookingValidationError,
    SaveButton,
    StatusBadge,
    TransactionStatus,
    ConfirmDeletionModal,
    DeleteIconButton,
    EditIconButton,
    BookingMiscAdditModal,
    CurrencyAmount,
    ObjectName,
    BookingProvider,
    BookingStatus,
    FormInput,
    RequestState,
    PageHeading,
    BackButton
  },
  props: ['bookingId'],
  data: () => ({
    /** @type {Promise<EntityHttpResponse<BookingBasicDto>>} */
    bookingRequest: null,
    /** @type {BookingDto} */
    booking: {updated: false},
    ownerBankDetails: null,

    /** @type {TransactionDto} */
    transaction: null,

    showBookingMiscModal: false,
    /** @type {MiscBookingDto} */
    bookingMiscToUpdate: null,
    /** @type {MiscBookingDto} */
    bookingMiscToDelete: null,

    showTransactionValidationErrorsModal: false,
    transactionValidationErrors: null,
    validationErrors: {},

    downloadingRentalAgreement: false,
    showCancelConfirmModal: false,
    showUncancelModal: false,
    showDeleteConfirmModal: false,
    hasEmails: false
  }),

  mounted() {
    if (this.bookingId) {
      this.reloadBooking();
    }
  },

  computed: {
    isBookingOpen() {
      return this.booking && this.booking.status === 0;
    },
    canSeeAllDetails() {
      return this.hasAnyPermission('BOOKING_VIEW_DETAILS');
    },
    canEditConsumptions() {
      return this.isBookingOpen && this.hasAnyPermission('BOOKING_CONSUMPTION_EDIT');
    },
    canViewMiscs() {
      return this.hasAnyPermission('MISC_BOOKING_VIEW');
    },
    canEditMiscs() {
      return (this.isBookingOpen || this.booking.canceled) && this.hasAnyPermission('MISC_BOOKING_EDIT');
    },
    provisionCalculationText() {
      if(this.booking.provision) {
        let provision = this.booking.provision;
        let netParts = [];

        let rentText = this.renderCurrencyAmount(provision.rent, 'EUR') + " " + this.trans('booking.provision.rent');
        rentText += ' (' + this.trans('booking.provision.' + (provision.rentIsNet ? 'net' : 'gross')) + ')';
        netParts.push(rentText);

        if(provision.includeAdditionalServices || provision.includePets) {
          let includeText = this.renderCurrencyAmount(provision.additionalServices, 'EUR') + ' ';
          let includes = [];
          if(provision.includeAdditionalServices) {
            includes.push(this.trans('booking.provision.additionalServices'));
          }
          if(provision.includePets) {
            includes.push(this.trans('booking.provision.pets'));
          }
          includeText += includes.join(', ');
          netParts.push(includeText);
        }

        let text = '( ' + netParts.join(' + ') + ' ) * ';
        text += this.renderCurrencyAmount(provision.commissionRate, '%', true, 0);

        if(provision.feeType === 1) {
          text += ' + ' + this.renderCurrencyAmount(provision.fee, 'EUR')
          text += ' ' + this.trans('booking.provision.feePerBooking')
        }

        return text;
      }
      return '';
    },
    provisionTaxText() {
      if(this.booking.provision) {
        return this.renderCurrencyAmount(this.booking.provision.taxRate, '%');
      }
      return '';
    },
    validationErrorsPresent() {
      return this.booking && this.booking.validationErrorCodes && this.booking.validationErrorCodes.length > 0;
    },
    pageTitle () {
      return this.transSingular('booking.booking') + ' #' + this.bookingId;
    },
    isOnlyCanceledInStayin () {
      return this.booking.canceled === false && this.booking.stayinCanceled === true
    },
    calculatedTransactionLineItems () {
      return this.booking.calculatedTransaction.lineItems
    }
  },

  methods: {
    reloadBooking() {
      this.bookingRequest = BookingApi.getById(this.bookingId)
        .then(response => {
          this.booking = response.data;
          if (this.$route.query && this.$route.query.downloadContract) {
            this.downloadRentalAgreement();
          }
        })
      this.reloadTransaction();
    },
    reloadTransaction() {
      BookingApi.getBookingTransaction(this.bookingId)
          .then(response => {
            this.transaction = response.data;
          })
          .catch(this.handleErrorResponseSilent)
    },
    reloadEmails() {
      this.$refs.emailsTable.loadData();
    },
    updateBooking() {
      BookingApi.updateBooking(this.bookingId, this.booking)
          .then(response => {
            this.validationErrors = {};
            this.booking = response.data;
            this.showSavedSuccessfullyNotification();
          })
          .catch((response) => this.validationErrors = this.getValidationErrors(response));
    },
    downloadRentalAgreement() {
      this.downloadingRentalAgreement = true;
      this.downloadFile('bookings/' + this.booking.id + '/rentalAgreement')
        .catch(this.handleErrorResponse)
        .finally(() => {
          this.downloadingRentalAgreement = false;
        })
      ;
    },
    deleteBookingMisc() {
      MiscApi.deleteBookingMisc(this.bookingMiscToDelete.miscId)
      .then(this.reloadBooking)
      .catch(this.handleErrorResponse)
    },
    showBookingMiscModalWithValue(value)
    {
      this.bookingMiscToUpdate = value;
      this.showBookingMiscModal = true;
    },
    openBooking() {
      BookingApi.openBooking(this.bookingId)
          .then(this.reloadBooking)
          .catch(this.handleErrorResponse);
    },
    closeBooking() {
      BookingApi.closeBooking(this.bookingId)
          .then(this.reloadBooking)
          .catch(this.handleErrorResponse);
    },
    cancelBooking () {
      BookingApi.cancelBooking(this.bookingId)
        .then(this.reloadBooking)
        .catch(this.handleErrorResponse);
    },
    deleteBooking () {
      BookingApi.delete(this.booking.id)
      .then(this.reloadBooking)
      .catch(this.handleErrorResponse);
    },
    restoreBooking () {
      BookingApi.restoreBooking(this.booking.id)
      .then(this.reloadBooking)
      .catch(this.handleErrorResponse);
    },
    createTransaction() {
      BookingApi.createBookingTransaction(this.bookingId)
          .then(this.reloadBooking)
          .catch((error) => {
            this.transactionValidationErrors = this.getValidationErrors(error, false);
            this.showTransactionValidationErrorsModal = true;
          });
    },
    markTransactionAsNotNeeded() {
      BookingApi.markBookingTransactionAsNotNeeded(this.bookingId)
        .then(this.reloadBooking)
        .catch(this.handleErrorResponse);
    },
    setHasEmails (page) {
      this.hasEmails = page.totalElements > 0;
    },

    onTransactionChanged () {
      this.reloadBooking();
    },

    onSurveyResultUpdate () {
      this.reloadBooking();
      this.reloadEmails();
    }
  }
}
</script>

<style>
</style>
